import React, { useRef, useEffect, useState } from 'react'
import Tags from './Tags'
import {
    Button,
    IconButton,
    Fade,
    Box,
    Tooltip,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import UploadIcon from '@mui/icons-material/Upload'
import CancelIcon from '@mui/icons-material/Cancel'
import TagIcon from '@mui/icons-material/Tag'
import DeleteIcon from '@mui/icons-material/Delete'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import { useFilterStore } from '../../stores/useFilterStore'
import { useZustand } from '../../hooks/useZustand'
import { useSummarize } from '../../hooks/useSummarize'
import { useSnackbar } from '../Snackbar'
import CopyIcon from '@mui/icons-material/ContentCopy'

const selectorShowSnackbar = state => state.showSnackbar
const selectorDoNotSaveGraph = state => state.doNotSaveGraph

const actionIconSx = {
    '& .MuiSvgIcon-root': { fontSize: '16px' },
}

// One footer action: a compact outlined button (icon + label), collapsing
// to a same-size bordered icon button when `iconOnly` (statement selection).
const FooterAction = ({ iconOnly, icon, label, tooltip, onClick, sx = {} }) => (
    <Tooltip title={tooltip}>
        {iconOnly ? (
            <IconButton
                onClick={onClick}
                size="small"
                aria-label={tooltip}
                sx={{
                    ...actionIconSx,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    padding: '5px',
                    ...sx,
                }}
            >
                {icon}
            </IconButton>
        ) : (
            <Button
                onClick={onClick}
                size="small"
                variant="outlined"
                startIcon={icon}
                sx={{
                    ...actionIconSx,
                    borderRadius: '8px',
                    textTransform: 'none',
                    minWidth: 0,
                    px: 1,
                    py: '3px',
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
                    ...sx,
                }}
            >
                {label}
            </Button>
        )}
    </Tooltip>
)

const Footer = ({
    selectedStatements,
    handleDeleteSelected,
    onOpen,
    open,
    isAuthoringDisabled,
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null)
    const [addingTagsMode, setAddingTagsMode] = React.useState(false)

    const filteredStatements = useFilterStore(state => state.filteredStatements)
    const selectedStatementsRef = useRef(selectedStatements)

    const [tags, setTags] = useState([])
    const [categoriesToAdd, setCategoriesToAdd] = useState([])
    const [categoriesToRemove, setCategoriesToRemove] = useState([])
    const [originalTags, setOriginalTags] = useState([])

    const openMenu = Boolean(anchorEl)

    const showSnackbar = useSnackbar(selectorShowSnackbar)
    const doNotSaveGraph = useZustand(selectorDoNotSaveGraph)

    const { handleSummarize, isFilteredView } = useSummarize()

    const hasSelection = selectedStatements.size > 0

    const handleImportClick = event => {
        setAnchorEl(event.currentTarget)
    }

    const handleCopySelected = () => {
        const textToCopy = filteredStatements
            .filter(statement => statement.visible)
            .filter(statement => statement.grayedOut != true)
            .map(statement => statement.text)
            .join('\n')

        navigator.clipboard
            .writeText(textToCopy)
            .then(() => {
                showSnackbar(
                    'success',
                    'Copied the visible statements to clipboard'
                )
            })
            .catch(err => {
                showSnackbar(
                    'error',
                    'Failed to copy the visible statements to clipboard'
                )
            })
    }

    const handleImportClose = option => {
        const currentContext = userFactory.getCurrentContext()
        const pinnedNodes = graphFactory.getPinnedNodes()
        const stopNodes = graphFactory.getStopNodes()
        const searchPhrase = pinnedNodes.join(' ')

        if (myEventEmitter._events['openImportDialog']) {
            switch (option) {
                case 'Upload a File':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'textFilesImport',
                        contextName: currentContext,
                        stopNodes: stopNodes.join('+'),
                    })
                    break
                case 'Upload a CSV':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'csvFileImport',
                        contextName: currentContext,
                        stopNodes: stopNodes.join('+'),
                    })
                    break
                case 'Google Results':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'google',
                        contextName: currentContext,
                        searchPhrase,
                    })
                    break
                case 'Search Intent':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'keywords',
                        contextName: currentContext,
                        searchPhrase,
                    })
                    break
                case 'Google Scholar':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'scholar',
                        contextName: currentContext,
                        searchPhrase,
                    })
                    break
                case 'Science Search':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'science',
                        contextName: currentContext,
                        searchPhrase,
                    })
                    break
                case 'Websites & URLs':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'urls',
                        contextName: currentContext,
                    })
                    break
                case 'RSS & Newsfeeds':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'rss',
                        contextName: currentContext,
                    })
                    break
                case 'AI-Generated':
                    myEventEmitter.emit('openImportDialog', {
                        dialogName: 'aiTopics',
                        contextName: currentContext,
                        searchPhrase,
                    })
                    break
                case 'More Sources...': {
                    const redirectUrl = `/import?context=${currentContext}&fornode=${pinnedNodes.join(
                        '+'
                    )}`
                    window.location.href = `${window.location.origin}${redirectUrl}`
                    break
                }
            }
        }
        setAnchorEl(null)
    }

    useEffect(() => {
        const extractCommonTags = statements => {
            if (!statements || statements.size === 0) return []
            const statementsTags = filteredStatements
                .filter(statement => statements.has(statement.uid))
                .map(statement => statement.tags)
            if (statementsTags.length === 0) return []
            // Start with the first array as the base
            const commonTags = statementsTags[0].filter(tag =>
                statementsTags.every(arr => arr.includes(tag))
            )
            return commonTags
        }

        const commonTags = extractCommonTags(selectedStatements)
        setTags(commonTags)
        setOriginalTags(commonTags)
        setCategoriesToAdd([])
        setCategoriesToRemove([])
    }, [selectedStatements])

    useEffect(() => {
        if (selectedStatements.size !== selectedStatementsRef.current.size) {
            setAddingTagsMode(false)
            selectedStatementsRef.current = selectedStatements
        }
    }, [selectedStatements])

    const importOptions = [
        'Upload a File',
        'Upload a CSV',
        'Google Results',
        'Search Intent',
        'Google Scholar',
        'Science Search',
        'Websites & URLs',
        'RSS & Newsfeeds',
        'AI-Generated',
        'More Sources...',
    ]

    const handleTagSelected = () => {
        setAddingTagsMode(!addingTagsMode)
    }

    const handleSaveTags = async () => {
        await useZustand
            .getState()
            .editCommonCategories(
                Array.from(selectedStatements),
                categoriesToAdd,
                categoriesToRemove
            )
        const successString =
            selectedStatements.size > 1
                ? `Statements have been successfully updated`
                : `The statement has been successfully updated`
        showSnackbar('success', successString)
        setAddingTagsMode(false)
    }

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 600,
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {addingTagsMode && (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 600,
                        mx: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '0 8px 0 8px',
                        gap: '8px',
                    }}
                >
                    <Tags
                        selectedTags={tags}
                        onTagsChange={newValues => {
                            setTags(newValues)
                            // Compute categoriesToAdd and categoriesToRemove
                            const toAdd = newValues.filter(
                                tag => !originalTags.includes(tag)
                            )
                            const toRemove = originalTags.filter(
                                tag => !newValues.includes(tag)
                            )
                            setCategoriesToAdd(toAdd)
                            setCategoriesToRemove(toRemove)
                        }}
                    />

                    <IconButton
                        onClick={handleSaveTags}
                        size="small"
                        color="info"
                        sx={{
                            '&:hover': {
                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                            },
                        }}
                    >
                        <SaveIcon />
                    </IconButton>
                </Box>
            )}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 600,
                    mx: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 0.75,
                    py: 0.5,
                }}
            >
                {hasSelection && !isAuthoringDisabled && (
                    <FooterAction
                        iconOnly
                        icon={<DeleteIcon />}
                        tooltip={`Delete ${selectedStatements.size} selected`}
                        onClick={handleDeleteSelected}
                        sx={{
                            color: '#d32f2f',
                            borderColor: 'rgba(211, 47, 47, 0.5)',
                        }}
                    />
                )}
                {hasSelection && (
                    <FooterAction
                        iconOnly
                        icon={<CopyIcon />}
                        tooltip={`Copy ${selectedStatements.size} selected`}
                        onClick={handleCopySelected}
                        sx={{ color: '#6688ff' }}
                    />
                )}
                {hasSelection && (
                    <FooterAction
                        iconOnly
                        icon={addingTagsMode ? <CancelIcon /> : <TagIcon />}
                        tooltip={`Tag ${selectedStatements.size} selected`}
                        onClick={handleTagSelected}
                        sx={{
                            color: addingTagsMode ? '#d32f2f' : '#6688ff',
                        }}
                    />
                )}
                {!isAuthoringDisabled && !doNotSaveGraph && (
                    <FooterAction
                        iconOnly={hasSelection}
                        icon={open ? <CancelIcon /> : <AddCircleIcon />}
                        label={open ? 'Close' : 'Add'}
                        tooltip={open ? 'Close' : 'Add New Statement'}
                        onClick={onOpen}
                        sx={
                            open
                                ? {
                                      color: '#d32f2f',
                                      borderColor: 'rgba(211, 47, 47, 0.5)',
                                  }
                                : {}
                        }
                    />
                )}
                {!isAuthoringDisabled && (
                    <FooterAction
                        iconOnly={hasSelection}
                        icon={<UploadIcon />}
                        label="Import"
                        tooltip="Import from different source"
                        onClick={handleImportClick}
                    />
                )}
                <FooterAction
                    iconOnly={hasSelection}
                    icon={<AutoAwesomeIcon />}
                    label={isFilteredView ? 'Summarize Visible' : 'Summarize'}
                    tooltip={
                        hasSelection
                            ? `AI: Summarize only the ${selectedStatements.size} selected statement(s)`
                            : 'AI: Use GPT AI to summarise the content of this graph based on your selections and filters'
                    }
                    onClick={handleSummarize}
                    sx={{
                        color: 'var(--ai-buttons-color)',
                        borderColor: 'var(--ai-buttons-color)',
                    }}
                />
            </Box>

            <Fade in={open} timeout={300}>
                <Box
                    position="absolute"
                    bottom="40px"
                    bgcolor="background.paper"
                    boxShadow={3}
                    width="324px"
                    borderRadius={2}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Menu
                            anchorEl={anchorEl}
                            open={openMenu}
                            onClose={handleImportClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                        >
                            {importOptions.map(option => (
                                <MenuItem
                                    key={option}
                                    onClick={() => {
                                        handleImportClose(option)
                                    }}
                                    sx={{
                                        minWidth: '200px',
                                        ...(option === 'More Sources...' && {
                                            borderTop: 1,
                                            borderColor: 'divider',
                                        }),
                                    }}
                                >
                                    {option}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Box>
            </Fade>
        </Box>
    )
}

export default Footer
